import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, CalendarDays, Share2, ArrowRight } from "lucide-react";
import api, { img } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { SectionTitle } from "@/components/Bits";

export default function Markets() {
  const { t, L } = useLang();
  const [markets, setMarkets] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/markets").then((r) => setMarkets(r.data)).catch(() => {});
  }, []);

  const share = (m) => {
    const url = `${window.location.origin}/markets/${m.id}`;
    if (navigator.share) navigator.share({ title: L(m.title_zh, m.title_en), url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success(L("链接已复制", "Link copied")); }
  };

  const shown = markets.filter((m) => filter === "all" || m.status === filter);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
      <SectionTitle tag="LATEST · 最新市集" title={t("markets_title")} />

      <div className="flex gap-2 mb-8">
        {["all", "upcoming", "past"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`market-filter-${f}`}
            className={`fk-border rounded-full px-4 py-2 text-sm font-semibold transition-colors ${filter === f ? "bg-black text-white" : "bg-white fk-lift"}`}
          >
            {t(f === "all" ? "all" : f)}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {shown.map((m, i) => (
          <motion.div
            key={m.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
            className="bg-white fk-border-thick rounded-2xl overflow-hidden fk-lift" data-testid={`market-card-${m.id}`}
          >
            <div className="relative">
              <img src={img(m.cover_image)} alt={m.title_en} className="w-full h-56 object-cover border-b-2 border-black" />
              <span className={`absolute top-3 left-3 font-mono-fk text-xs fk-border rounded-full px-3 py-1 ${m.status === "upcoming" ? "bg-[#00E5FF]" : "bg-white"}`}>
                {t(m.status === "upcoming" ? "upcoming" : "past")}
              </span>
            </div>
            <div className="p-6">
              <h3 className="font-display text-2xl">{L(m.title_zh, m.title_en)}</h3>
              <div className="mt-3 space-y-1.5 font-mono-fk text-sm text-black/70">
                <div className="flex items-center gap-2"><CalendarDays size={15} /> {m.date}</div>
                <div className="flex items-center gap-2"><MapPin size={15} /> {L(m.location_zh, m.location_en)}</div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <Link to={`/markets/${m.id}`} className="fk-border rounded-full px-4 py-2 text-sm font-semibold bg-[#FFD93D] fk-lift flex items-center gap-1">
                  {t("view_detail")} <ArrowRight size={14} />
                </Link>
                {m.apply_open && (
                  <Link to={`/apply?market=${m.id}`} className="fk-border rounded-full px-4 py-2 text-sm font-semibold bg-white fk-lift">
                    {t("apply_join")}
                  </Link>
                )}
                <button onClick={() => share(m)} className="fk-border rounded-full p-2 bg-white fk-lift" aria-label="share"><Share2 size={16} /></button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {shown.length === 0 && <p className="text-black/50 font-mono-fk mt-6">暂无市集 / No markets.</p>}
    </div>
  );
}
